<template>
  <div ref="resourcesContainer">
    <ContainerLayout class="tw:py-11 tw:flex tw:flex-col">
      <InsightsHeader title="plus_exclusive_resources_header" subtitle="plus_video_subtext" />
      <div class="tw:flex tw:flex-col tw:gap-y-6 tw:w-full">
        <!-- Video Card -->
        <div class="tw:bg-white tw:rounded-xl tw:p-6">
          <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-4 tw:md:gap-6">
            <div class="tw:flex tw:flex-col tw:gap-1 tw:md:flex-shrink-0 tw:mr-12 tw:md:w-5/12 tw:md:self-center">
              <h2 class="tw:font-bold tw:md:text-xl">
                {{ $t('plus_video_header') }}
              </h2>
              <p class="tw:text-sm">{{ $t('plus_video_subtext') }}</p>
            </div>
            <div class="tw:w-full tw:md:w-7/12 tw:min-w-0">
              <div class="tw:relative tw:w-full tw:pb-[65.25%]">
                <div
                  data-testid="plus-insights-video-player"
                  ref="playerContainerRef"
                  class="tw:absolute tw:inset-0 tw:rounded-xl tw:overflow-hidden"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- PDFs Card -->
        <div
          class="tw:bg-white tw:rounded-xl tw:p-6 tw:flex tw:flex-col tw:md:flex-row tw:md:items-center tw:gap-y-4 tw:w-full tw:justify-between"
        >
          <div class="tw:order-1 tw:md:order-1">
            <img :src="ResourcesIcon" alt="" role="presentation" class="tw:mb-4" />
            <div class="tw:flex tw:flex-col tw:gap-y-1">
              <h2 id="guide-heading" class="tw:font-bold tw:text-xl">
                {{ $t('plus_guide_title') }}
              </h2>
              <p class="tw:text-sm">
                {{ $t('plus_guide_subtext') }}
              </p>

              <a
                :href="guideFileName"
                @click="logEvents"
                target="_blank"
                rel="noopener noreferrer"
                class="tw:hidden tw:md:inline-flex tw:text-blue-700 tw:font-bold tw:text-sm"
                :aria-label="`${$t('tip_plus_guide_placeholder')} (PDF, ${$t('opens in new tab')})`"
              >
                {{ $t('tip_plus_guide_placeholder') }}
              </a>
            </div>
          </div>

          <img
            :src="ScreenshotImage"
            alt="Preview of the Business Studio Plus guide"
            class="tw:w-full tw:md:max-w-[247px] tw:order-2 tw:md:order-2"
          />

          <a
            :href="guideFileName"
            target="_blank"
            @click="logEvents"
            rel="noopener noreferrer"
            class="tw:flex tw:md:hidden tw:text-blue-700 tw:font-bold tw:text-sm tw:order-3"
            aria-hidden="true"
            tabindex="-1"
          >
            {{ $t('tip_plus_guide_placeholder') }}
          </a>
        </div>
      </div>
    </ContainerLayout>
  </div>
</template>
<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'

import ContainerLayout from '@/layouts/ContainerLayout.vue'
import InsightsHeader from '@/components/studioPlus/insights/InsightsHeader.vue'
import Player from '@vimeo/player'

import { selectedAccommodationId } from '@/layouts/queries'
import { useSessionValues } from '@/composables/useSessionValues'

import appAnalytics from '@/utils/tracking'

const ResourcesIcon = new URL('/src/assets/img/studio-plus-insights/resources-icon.svg', import.meta.url)
const ScreenshotImage = new URL('/src/assets/img/studio-plus-insights/screenshot_image.png', import.meta.url)

const { language } = useSessionValues()
const guideFileName = computed(() => {
  const defaultFileName = `/home/BusinessStudioPlusGuide.pdf`
  const localeFileNames = {
    'de-DE': '/home/BusinessStudioPlusGuide_de.pdf',
    'it-IT': '/home/BusinessStudioPlusGuide_it.pdf',
    'fr-FR': '/home/BusinessStudioPlusGuide_fr.pdf',
    'es-ES': '/home/BusinessStudioPlusGuide_es.pdf',
    'es-MX': '/home/BusinessStudioPlusGuide_mx.pdf',
    'pt-BR': '/home/BusinessStudioPlusGuide_br.pdf',
    'pt-PT': '/home/BusinessStudioPlusGuide_pt.pdf',
    'el-GR': '/home/BusinessStudioPlusGuide_el.pdf',
    'tr-TR': '/home/BusinessStudioPlusGuide_tr.pdf',
  }

  return localeFileNames[language.value] ?? defaultFileName
})

let player = null
const playerContainerRef = ref(null)
const videoUrl = computed(() => `https://player.vimeo.com/video/1158349476?h=09ce1b6cfd`)
const initPlayer = () => {
  if (!playerContainerRef.value) return

  if (player) {
    player.destroy()
    player = null
  }

  player = new Player(playerContainerRef.value, {
    url: videoUrl.value,
    autoplay: false,
    muted: true,
    controls: true,
    responsive: true,
    autopause: false,
    badge: false,
  })

  player.on('play', () => {
    appAnalytics.track(appAnalytics.events.SP_PLUS_INSIGHTS_VIDEO_PLAYED, {
      item_id: selectedAccommodationId.value,
    })
  })

  player.on('ended', () => {
    appAnalytics.track(appAnalytics.events.SP_PLUS_INSIGHTS_VIDEO_COMPLETED, {
      item_id: selectedAccommodationId.value,
    })
  })
}

const resourcesContainer = ref(null)
const isInViewport = ref(false)
let observer = null

onMounted(() => {
  initPlayer()
  observer = new IntersectionObserver(
    ([entry]) => {
      isInViewport.value = entry.isIntersecting

      if (entry.isIntersecting) {
        appAnalytics.track(appAnalytics.events.SP_PLUS_INSIGHTS_RESOURCES_VIEWED, {
          item_id: selectedAccommodationId.value,
        })
        if (observer) {
          observer.disconnect()
          observer = null
        }
      }
    },
    { threshold: 0.1 }
  )

  if (resourcesContainer.value) {
    observer.observe(resourcesContainer.value)
  }
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
})

const logEvents = () => {
  appAnalytics.track(appAnalytics.events.SP_PLUS_INSIGHTS_GUIDE_DOWNLOADED, {
    item_id: selectedAccommodationId.value,
    guide: guideFileName.value?.split('/').pop(),
  })
}
</script>
